import { Button } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { BannerPortal, Boundary, Page } from '~/components'
import { BpSpinner, Spinner } from '~/elements'
import { useAnnouncements } from '~/features/announcements/hooks/useAnnouncement'
import { SaleEventBannerWrapper } from '~/features/billing/components/SaleEventBanner'
import { AuditsPreview, BotsView } from '~/features/home/components'
import { WorkspaceSocialHeader } from '~/features/workspaces/components'
import { useCreateBotFlow } from '~/hooks/bots'
import { SidebarLayout } from '~/layouts'
import { userPreferencesQueryOptions } from '~/queries'
import { getQueryOptions, useSuspenseQuery } from '~/services'
import { BotsUsage } from '../../../features/home/components/BotsUsage'
import { MembersPreview } from '../../../features/home/components/MembersPreview'

export const Route = createFileRoute('/workspaces/$workspaceId/home')({
  component: WorkspaceComponent,
})

function WorkspaceComponent() {
  const { workspaceId } = Route.useParams()
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const { user } = Route.useRouteContext()
  const { data: bots = [] } = useSuspenseQuery('workspaces_/$workspaceId_/bots_', { workspaceId })
  const { startCreateBotFlow, isCreatingBot } = useCreateBotFlow({ workspaceId })

  const { data: recentBots } = useTanstackSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/botHistory', params: { workspaceId } })
  )

  useAnnouncements({
    workspaceId,
    botId: recentBots[0],
    targetAnnouncementIds: [
      'conversation-insights',
      'billing-refresh',
      'webchat-config-refresh',
      'webchat-v3.4',
      'auto-recharge-addons',
      'webchat-v3.5',
    ],
  })

  return (
    <Boundary showErrorIcon errorIconSize={5} loader={<BpSpinner />}>
      <Page
        title={
          <div className="flex items-start md:items-center">
            <WorkspaceSocialHeader workspace={workspace} />
          </div>
        }
        actions={
          <div className="hidden w-fit items-center sm:flex">
            <MembersPreview workspaceId={workspaceId} />
          </div>
        }
        primaryActions={
          <Button onClick={startCreateBotFlow} disabled={isCreatingBot} className="h-full w-full gap-2 md:w-fit">
            {isCreatingBot ? <Spinner size="2" color="white" /> : <Plus />}
            Create Bot
          </Button>
        }
      >
        <BannerPortal>
          <Boundary onError={() => null} suspenseFallback={null}>
            <SaleEventBannerWrapper workspaceId={workspaceId} />
          </Boundary>
        </BannerPortal>
        <SidebarLayout
          main={
            <div className="flex flex-col gap-4">
              <Boundary showErrorIcon errorIconSize={5}>
                <BotsView bots={bots} workspaceId={workspaceId} userId={user.id} />
              </Boundary>

              <div className="mt-2 flex flex-col gap-4 lg:hidden">
                <Boundary className="h-48" loaderSize="7" onError={() => null}>
                  <BotsUsage workspaceId={workspaceId} />
                </Boundary>
                <Boundary className="h-80" loaderSize="7">
                  <AuditsPreview workspaceId={workspaceId} />
                </Boundary>
              </div>
            </div>
          }
          rightSidebar={
            <div className="hidden flex-col gap-6 lg:flex">
              <Boundary className="h-48" loaderSize="7" onError={() => null}>
                <BotsUsage workspaceId={workspaceId} />
              </Boundary>
              <Boundary className="h-80" loaderSize="7">
                <AuditsPreview workspaceId={workspaceId} />
              </Boundary>
            </div>
          }
        />
      </Page>
    </Boundary>
  )
}
